import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PartnerServiceInterface, SubscriptionTierInterface, SubscriptionTiersService } from '@galaxy/partner';
import { BusinessCenterApiService } from '@vendasta/partner';
import { Observable } from 'rxjs';
import { map, publishReplay, refCount, switchMap } from 'rxjs/operators';
import { ApiService } from '../api-service/api.service';
import { AppConfigService } from '../app-config.service';
import { PartnerAjaxResponse } from './partner/partner-ajax.response';
import { PartnerInfo } from './partner/partner-info';

@Injectable({ providedIn: 'root' })
export class PartnerService implements PartnerServiceInterface {
  private canCustomizeWhitelabel: boolean;
  private marketAccess: string[];

  readonly subscriptionTier$: Observable<SubscriptionTierInterface>;
  readonly partnerConfig$: Observable<PartnerAjaxResponse>;
  readonly partnerNotificationEnabled$ = this.configService.config$.pipe(
    map((config) => config.partnerId),
    switchMap((partnerId) => this.businessCenterApiService.getMarketConfiguration({ partnerId })),
    map((response) => response.marketConfiguration.notificationsEnabled),
  );

  readonly businessCenterHostname$: Observable<string>;

  constructor(
    private apiService: ApiService,
    private configService: AppConfigService,
    private http: HttpClient,
    private router: Router,
    private readonly subscriptionService: SubscriptionTiersService,
    private readonly businessCenterApiService: BusinessCenterApiService,
  ) {
    this.configService.config$.subscribe((config) => {
      this.canCustomizeWhitelabel = config.userCanCustomizeWhitelabel;
      this.marketAccess = config.accessibleMarketIds;
    });

    this.subscriptionTier$ = this.configService.config$.pipe(
      switchMap((config) => this.getPartnerByPartnerId(config.partnerId)),
      switchMap((partner) => this.subscriptionService.getSubscriptionTier(partner.subscription_tier, partner.pid)),
      publishReplay(1),
      refCount(),
    );
    this.partnerConfig$ = this.configService.config$.pipe(
      switchMap((config) => this.getPartnerByPartnerId(config.partnerId)),
      publishReplay(1),
      refCount(),
    );
    this.businessCenterHostname$ = this.configService.config$.pipe(
      switchMap(() => {
        return this.apiService.get('/_ajax/v1/marketplace/partner/get-business-center-hostname');
      }),
      publishReplay(1),
      refCount(),
    );
  }

  get CanCustomizeWhitelabel(): boolean {
    return this.canCustomizeWhitelabel;
  }

  get MarketAccess(): string[] {
    return this.marketAccess;
  }

  public getBusinessCenterHostname(): Observable<string> {
    return this.businessCenterHostname$;
  }

  public get partnerId(): string {
    return this.configService.config?.partnerId;
  }

  public getPartnerId(): Observable<string> {
    return this.configService.config$.pipe(map((config) => config.partnerId));
  }

  public search(term: string): Observable<PartnerInfo[]> {
    const url = `/api/v3/partner/search/?searchTerm=${term}`;
    return this.apiService.get(url).pipe(map((data) => data.partners));
  }

  getPartnersByPartnerId(partnerIds: string[]): Observable<any> {
    const url = '/_ajax/v1/partners/';
    return this.apiService.post(url, { partnerId: partnerIds });
  }

  getPartnerByPartnerId(partnerId: string): Observable<PartnerAjaxResponse> {
    const url = '/_ajax/v1/partner/?partnerId=' + partnerId;
    return this.apiService.get(url);
  }

  getPartnerIds(partnerIds: string[], limit: number, offset: number): Observable<any[]> {
    const params = { partnerIds: partnerIds, limit: limit, offset: offset };
    return this.apiService.get('/_ajax/v1/partner/ids', { params: params });
  }

  updateSubscription(level: string): Observable<any> {
    const url = '/ajax/v1/superadmin/partner/subscription/update';
    const data = { partnerId: this.partnerId, subscriptionLevel: level };
    return this.apiService.post(url, data);
  }

  upgradeSubscription(level: string): Observable<any> {
    const url = '/api/v3/partner/subscription/upgrade/';
    const data = { partnerId: this.partnerId, subscriptionLevel: level };
    return this.http.post(url, data, { withCredentials: true });
  }

  switchPartner(partnerId: string, route?: string): void {
    this.configService.load(partnerId);
    if (route) {
      this.router.navigateByUrl(route);
    }
  }
}
